import type React from "react";
import { useEffect, useState } from "react";
import { Image, Text, type TextStyle, View } from "react-native";
import { tokens } from "../../design-system/tokens";
import { useCachedImageUri } from "../../sync/imageByteCache";

export type AvatarSize = "small" | "medium" | "large" | "tile" | "company";
export type AvatarShape = "circle" | "rounded";
export type PresenceStatus = "online" | "offline" | "away" | null;

export interface AvatarProps {
  size?: AvatarSize;
  /**
   * `rounded` = squircle with size-specific radius (matches the new
   * Figma design for chat rows and company rows). `circle` = pill,
   * matches the legacy avatars used throughout chat message rows.
   */
  shape?: AvatarShape;
  imageUri?: string;
  initials?: string;
  presence?: PresenceStatus;
  accessibilityLabel?: string;
}

// Dimension + per-size rounded-corner radius (used when `shape ==='rounded'`).
const SIZE_TABLE: Record<
  AvatarSize,
  { px: number; rounded: number; initials: keyof typeof initialsBySize }
> = {
  company: { px: 28, rounded: 6, initials: "small" },
  small: { px: 32, rounded: 8, initials: "small" },
  tile: { px: 40, rounded: 12, initials: "medium" },
  medium: { px: 44, rounded: 12, initials: "medium" },
  large: { px: 64, rounded: 16, initials: "large" },
};

// Presence-dot dimensions for each avatar size.
const PRESENCE_TABLE: Record<AvatarSize, { wh: number; offset: number }> = {
  company: { wh: 8, offset: -1 },
  small: { wh: 10, offset: -1 },
  tile: { wh: 8, offset: -1 },
  medium: { wh: 12, offset: 0 },
  large: { wh: 16, offset: 2 },
};

// CLASS-BASED theming: dark mode is driven by `data-theme="dark"` + the `.dark`
// class on `<html>`, NOT the OS color scheme. We render with `dark:` variant
// classes (values mirror `tokens.color.semantic` light/dark) so there is ZERO
// per-instance theme subscription — this matters because Avatar renders many
// times at once (e.g. every participant tile in a call grid), and a hook-based
// `useColorScheme` would add a listener + a re-render path per avatar.
const PRESENCE_DOT: Record<NonNullable<PresenceStatus>, string> = {
  online: "bg-[#3CCB4F]", // presence.online (same light/dark)
  offline: "bg-[#8E8E93]", // text.tertiary (same light/dark)
  away: "bg-[#D67D2F] dark:bg-[#F29E51]", // accent.amber
};

export const Avatar: React.FC<AvatarProps> = ({
  size = "medium",
  shape = "circle",
  imageUri,
  initials,
  presence = null,
  accessibilityLabel,
}) => {
  const { px, rounded } = SIZE_TABLE[size];
  const radius = shape === "circle" ? tokens.radius.pill : rounded;
  const presenceCfg = PRESENCE_TABLE[size];
  // Resolve through the persistent image-byte cache: on reload a previously
  // shown avatar renders instantly from cached bytes (no black flash while the
  // presigned S3 URL re-downloads). `undefined` until the sub-ms cache check
  // resolves, so the initials placeholder shows instead of a loading <img>.
  const cachedImageUri = useCachedImageUri(imageUri);

  // Fall back to the initials when the image fails to LOAD (an expired presigned
  // S3 URL 403s after ~5 min, a transient network error, a deleted media id).
  // Without this the <Image> renders blank and the avatar "disappears" — the
  // exact "group avatar sometimes just doesn't show" symptom. Reset on every uri
  // change so a freshly-resolved url gets a clean attempt.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => {
    setImageFailed(false);
  }, [cachedImageUri]);
  const showImage = cachedImageUri !== undefined && !imageFailed;

  // Only the runtime-computed bits (per-size dimensions / radius / font metrics)
  // stay inline; every color is a class so the theme follows `.dark`.
  const initialsTextStyle: TextStyle = {
    fontFamily: tokens.typography.fontFamily.base,
    ...initialsBySize[SIZE_TABLE[size].initials],
  };

  const displayInitials = initials
    ? initials
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "";

  return (
    <View
      className="relative items-center justify-center bg-[#EFEFF0] dark:bg-[#3A3A3C]"
      style={{ width: px, height: px, borderRadius: radius }}
      accessibilityRole="image"
      accessibilityLabel={
        accessibilityLabel ??
        (initials ? `Avatar for ${initials}` : "User avatar")
      }
    >
      {showImage ? (
        <Image
          source={{ uri: cachedImageUri }}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: radius,
          }}
          onError={() => setImageFailed(true)}
          accessibilityIgnoresInvertColors
        />
      ) : (
        <Text
          className="text-center font-semibold text-[#1C1C1E] dark:text-white"
          style={initialsTextStyle}
        >
          {displayInitials}
        </Text>
      )}
      {presence ? (
        <View
          className={`absolute rounded-full border-2 border-white dark:border-[#111318] ${PRESENCE_DOT[presence]}`}
          style={{
            width: presenceCfg.wh,
            height: presenceCfg.wh,
            bottom: presenceCfg.offset,
            right: presenceCfg.offset,
          }}
        />
      ) : null}
    </View>
  );
};

const initialsBySize: Record<"small" | "medium" | "large", TextStyle> = {
  small: {
    fontSize: tokens.typography.fontSize.caption,
    lineHeight:
      tokens.typography.fontSize.caption * tokens.typography.lineHeight.tight,
  },
  medium: {
    fontSize: tokens.typography.fontSize.body,
    lineHeight:
      tokens.typography.fontSize.body * tokens.typography.lineHeight.tight,
  },
  large: {
    fontSize: tokens.typography.fontSize.headline,
    lineHeight:
      tokens.typography.fontSize.headline * tokens.typography.lineHeight.tight,
  },
};
